import { BLOCK_NAMES, BLOCK_TYPES } from './constants';

const STORAGE_KEY = 'resourceCalendarConfig';
const BACKUP_KEY  = 'resourceCalendarConfigBackup';

// ─── Time helpers ────────────────────────────────────────────────────────────

const toMinutes = (time) => {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
};

const fromMinutes = (total) => {
  const h = Math.floor(total / 60);
  const m = total % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

// ─── Date helpers ─────────────────────────────────────────────────────────────

/**
 * Returns a local YYYY-MM-DD string — never uses UTC (avoids off-by-one on
 * machines west of UTC+0).
 */
export const getDateKey = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

// ─── Interval helpers ─────────────────────────────────────────────────────────

export const createInterval = (start, end, type) => ({ start, end, type });

export const mergeIntervals = (intervals) => {
  if (intervals.length === 0) return [];

  const sorted = [...intervals].sort(
    (a, b) => toMinutes(a.start) - toMinutes(b.start)
  );
  const merged = [{ ...sorted[0] }];

  for (let i = 1; i < sorted.length; i += 1) {
    const last    = merged[merged.length - 1];
    const current = sorted[i];
    const lastEnd      = toMinutes(last.end);
    const currentStart = toMinutes(current.start);

    if (currentStart <= lastEnd && last.type === current.type) {
      const currentEnd = toMinutes(current.end);
      if (currentEnd > lastEnd) last.end = current.end;
    } else {
      merged.push({ ...current });
    }
  }

  return merged;
};

export const subtractBreaks = (workIntervals, breakIntervals) => {
  if (breakIntervals.length === 0) return workIntervals;

  const breakRanges = mergeIntervals(breakIntervals).map((iv) => ({
    start: toMinutes(iv.start),
    end:   toMinutes(iv.end),
  }));

  return workIntervals.flatMap((work) => {
    let segments = [{ start: toMinutes(work.start), end: toMinutes(work.end) }];

    breakRanges.forEach((br) => {
      segments = segments.flatMap((seg) => {
        if (br.end <= seg.start || br.start >= seg.end) return [seg];
        const next = [];
        if (br.start > seg.start) next.push({ start: seg.start, end: br.start });
        if (br.end   < seg.end)   next.push({ start: br.end,    end: seg.end  });
        return next;
      });
    });

    return segments
      .filter((seg) => seg.end > seg.start)
      .map((seg) => ({
        start: fromMinutes(seg.start),
        end:   fromMinutes(seg.end),
        type:  BLOCK_TYPES.WORK,
      }));
  });
};

export const normalizeIntervals = (intervals) => {
  const work  = mergeIntervals(intervals.filter((iv) => iv.type === BLOCK_TYPES.WORK));
  const brk   = mergeIntervals(intervals.filter((iv) => iv.type === BLOCK_TYPES.BREAK));
  const adjWork = subtractBreaks(work, brk);

  return [...adjWork, ...brk].sort((a, b) => {
    const diff = toMinutes(a.start) - toMinutes(b.start);
    return diff !== 0 ? diff : a.type.localeCompare(b.type);
  });
};

export const sortIntervals = (intervals) => [...intervals].sort((a, b) => {
  const diff = toMinutes(a.start) - toMinutes(b.start);
  if (diff !== 0) return diff;
  return toMinutes(a.end) - toMinutes(b.end);
});

// ─── Rule helpers ─────────────────────────────────────────────────────────────

export const getDefaultRulesForDate = (rules, date) => {
  const day = date.getDay();
  return rules.filter((rule) => rule.daysOfWeek.includes(day));
};

export const buildDayIntervals = (defaultRules, override) => {
  const sourceIntervals = defaultRules.flatMap((rule) => rule.intervals || []);
  const manualIntervals = override?.enabled ? (override.intervals || []) : [];
  return sortIntervals([
    ...normalizeIntervals(sourceIntervals),
    ...sortIntervals(manualIntervals),
  ]);
};

// ─── Calendar event conversion ────────────────────────────────────────────────

/**
 * Converts a day's intervals into FullCalendar event objects.
 * dateKey MUST be provided — keeping it required avoids the
 * "used before defined" bug that existed when getDateKey was the default arg.
 */
export const toCalendarEvents = (date, intervals, dateKey, options = {}) => {
  const key = dateKey ?? getDateKey(date);
  const { source = 'override', editable = true, idPrefix = '' } = options;

  return intervals.map((interval, index) => ({
    id:    interval.id || `${idPrefix}${key}::${index}`,
    title: interval.title || BLOCK_NAMES[interval.type] || (interval.type === BLOCK_TYPES.WORK ? 'Work' : 'Break'),
    start: `${key}T${interval.start}`,
    end:   `${key}T${interval.end}`,
    allDay: false,
    type:   interval.type,
    editable: interval.editable ?? editable,
    extendedProps: {
      dateKey:       key,
      intervalIndex: index,
      itemId:        interval.id || `${idPrefix}${key}::${index}`,
      type:          interval.type,
      details:       interval.details || '',
      originalStart: interval.start,
      originalEnd:   interval.end,
      originalTitle: interval.title || BLOCK_NAMES[interval.type] || (interval.type === BLOCK_TYPES.WORK ? 'Work' : 'Break'),
      originalType:  interval.type,
      source,
    },
  }));
};

// ─── Persistence ──────────────────────────────────────────────────────────────

export const defaultConfig = {
  defaultRules: [
    {
      id:         'weekday-default',
      name:       'Weekday Rule',
      daysOfWeek: [1, 2, 3, 4, 5],
      intervals: [],
    },
  ],
  overrides: {},
};

export const loadConfig = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : defaultConfig;
  } catch {
    return defaultConfig;
  }
};

export const saveConfig = (config) => {
  try {
    const previous = localStorage.getItem(STORAGE_KEY);
    if (previous) localStorage.setItem(BACKUP_KEY, previous);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
  } catch (err) {
    console.warn('Failed to save Resource Calendar config:', err);
  }
};

export const restoreBackup = () => {
  try {
    const raw = localStorage.getItem(BACKUP_KEY);
    if (!raw) return null;
    const backup = JSON.parse(raw);
    saveConfig(backup);
    return backup;
  } catch {
    return null;
  }
};
